// ---- Overhead player HP bars ----
// A floating HP bar over each remote player who is currently in combat. Reuses the
// same screen-projection pattern as the NPC overhead bars / speech bubbles
// (`_v.set(world).project(camera)`), and a pooled set of DOM bars keyed by player
// id so they're created/removed as players enter/leave combat.
//
// Only REMOTE players get a bar — the local player reads their own HP off the
// bottom-right vitals HUD, so an over-the-head bar in front of the camera would
// just be in the way. net.ts builds the in-combat list; combat.ts (which owns the
// camera + the per-frame projection pass) drives the render.

import * as THREE from "three";

/** One in-combat player to draw a bar for. (wx,wy,wz) is the world anchor point. */
export type PlayerBar = {
  id: string; wx: number; wy: number; wz: number; hp: number; maxHp: number;
  snare?: boolean; stun?: boolean; slow?: boolean;
};

/** World height above a player's feet to float the bar (head ≈ 2.0, clear of it). */
export const PLAYER_BAR_Y = 3.0;

const _v = new THREE.Vector3();
let container: HTMLElement | null = null;

// ---- Shared projected-bar write path (diff-before-write, the hud.ts idiom) --
// ONE copy of "project a world anchor → position a floating DOM bar → set its
// fill(s)", with every style write gated on a last-written cache so unchanged
// frames skip the CSSOM entirely. Used by the remote-player bars below AND the
// NPC overhead HP/STA bars in combat.ts — a rounding/threshold tweak lands here
// once. Two fill slots so an hp + stamina pair shares one cache object.

export type BarCss = {
  disp: string; l: number; t: number;
  f1: number; f2: number;
  /** Last-written label text for fill slots (e.g. "10/10"). */
  t1: string; t2: string;
};
export function freshBarCss(): BarCss {
  return { disp: "", l: NaN, t: NaN, f1: NaN, f2: NaN, t1: "", t2: "" };
}
/** Hide the bar (guarded — no write when already hidden). */
export function hideBar(el: HTMLElement, c: BarCss): void {
  if (c.disp !== "none") { c.disp = "none"; el.style.display = "none"; }
}
/** Show + position a projected bar: NDC → whole-px left/top, all writes
 *  diff-gated. Returns false (and hides it) when the anchor is behind the
 *  camera — callers skip the fills on false. */
export function placeBar(el: HTMLElement, c: BarCss, v: THREE.Vector3, W: number, H: number): boolean {
  if (v.z > 1) { hideBar(el, c); return false; }
  if (c.disp !== "block") { c.disp = "block"; el.style.display = "block"; }
  const l = Math.round((v.x * 0.5 + 0.5) * W), t = Math.round((-v.y * 0.5 + 0.5) * H);
  if (l !== c.l) { c.l = l; el.style.left = `${l}px`; }
  if (t !== c.t) { c.t = t; el.style.top = `${t}px`; }
  return true;
}
/** Diff-gated fill width: frac 0..1 → % in 0.1 steps, clamped 0..100. */
export function fillBar(el: HTMLElement, c: BarCss, slot: "f1" | "f2", frac: number): void {
  const w = Math.max(0, Math.min(100, Math.round(frac * 1000) / 10));
  if (w !== c[slot]) { c[slot] = w; el.style.width = `${w}%`; }
}

/** Diff-gated numeric readout inside a bar ("10/10"). Rounds for display. */
export function labelBar(
  el: HTMLElement | null | undefined,
  c: BarCss,
  slot: "t1" | "t2",
  cur: number,
  max: number,
): void {
  if (!el) return;
  const text = `${Math.max(0, Math.round(cur))}/${Math.max(0, Math.round(max))}`;
  if (c[slot] !== text) { c[slot] = text; el.textContent = text; }
}

/** Build a bar shell with fill + centered numeric label (NPC + remote players). */
export function makeCbar(kind: "hp" | "sta"): {
  bar: HTMLDivElement; fill: HTMLDivElement; label: HTMLSpanElement;
} {
  const bar = document.createElement("div");
  bar.className = `cbar ${kind}`;
  const fill = document.createElement("div");
  fill.className = "cfill";
  const label = document.createElement("span");
  label.className = "clabel";
  label.textContent = "0/0";
  bar.append(fill, label);
  return { bar, fill, label };
}

/** Status icon row under combat bars (snare ⛓ / stun 💫 / slow 🐌). */
export function makeStatusIcons(): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "cstatus";
  row.style.display = "none";
  const snare = document.createElement("span");
  snare.className = "cstatus-i snare";
  snare.textContent = "⛓";
  snare.title = "Snared";
  snare.style.display = "none";
  const stun = document.createElement("span");
  stun.className = "cstatus-i stun";
  stun.textContent = "💫";
  stun.title = "Stunned";
  stun.style.display = "none";
  const slow = document.createElement("span");
  slow.className = "cstatus-i slow";
  slow.textContent = "🐌";
  slow.title = "Slowed";
  slow.style.display = "none";
  row.append(snare, stun, slow);
  return row;
}

/** Show/hide snare/stun/slow icons on a status row. */
export function setStatusIcons(
  row: HTMLElement | null | undefined,
  snare: boolean,
  stun: boolean,
  slow = false,
): void {
  if (!row) return;
  const sn = row.querySelector(".snare") as HTMLElement | null;
  const st = row.querySelector(".stun") as HTMLElement | null;
  const sl = row.querySelector(".slow") as HTMLElement | null;
  if (sn) sn.style.display = snare ? "inline" : "none";
  if (st) st.style.display = stun ? "inline" : "none";
  if (sl) sl.style.display = slow ? "inline" : "none";
  row.style.display = snare || stun || slow ? "flex" : "none";
}

type Bar = { wrap: HTMLDivElement; fill: HTMLDivElement; label: HTMLSpanElement; icons: HTMLDivElement; css: BarCss };
const bars = new Map<string, Bar>();

export function initPlayerBars(): void {
  container = document.getElementById("playerbars");
}

function makeBar(): Bar {
  const wrap = document.createElement("div");
  wrap.className = "pbar-wrap";
  const { bar, fill, label } = makeCbar("hp"); // share the NPC bar styling
  const icons = makeStatusIcons();
  wrap.append(bar, icons);
  container!.appendChild(wrap);
  return { wrap, fill, label, icons, css: freshBarCss() };
}

/** Project + position an HP bar over every in-combat player in `list`, and drop the
 *  DOM for any player no longer present. Call ONCE per frame with the FULL set
 *  (a partial list would prune the others' bars). */
export function renderPlayerBars(camera: THREE.Camera, list: PlayerBar[]): void {
  if (!container) return;
  const W = window.innerWidth, H = window.innerHeight;
  const seen = new Set<string>();
  for (const e of list) {
    seen.add(e.id);
    let b = bars.get(e.id);
    if (!b) { b = makeBar(); bars.set(e.id, b); }
    _v.set(e.wx, e.wy, e.wz).project(camera);
    if (!placeBar(b.wrap, b.css, _v, W, H)) continue; // behind the camera
    fillBar(b.fill, b.css, "f1", e.hp / e.maxHp);
    labelBar(b.label, b.css, "t1", e.hp, e.maxHp);
    setStatusIcons(b.icons, !!e.snare, !!e.stun, !!e.slow);
  }
  for (const [id, b] of bars) {
    if (!seen.has(id)) { b.wrap.remove(); bars.delete(id); }
  }
}
